import * as React from "react";
import { Select as BaseSelect } from "@base-ui/react/select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../lib/cn";

export const Select = BaseSelect.Root;
export const SelectValue = BaseSelect.Value;

export const SelectTrigger = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof BaseSelect.Trigger>>(
  ({ className, children, ...props }, ref) => (
    <BaseSelect.Trigger
      ref={ref}
      className={cn(
        "flex h-11 w-full items-center justify-between rounded-md border border-charcoal-200 bg-surface px-3.5 text-sm text-charcoal-800",
        "transition-colors focus-visible:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500/20",
        "data-[placeholder]:text-charcoal-300 data-[popup-open]:border-primary-500",
        className,
      )}
      {...props}
    >
      {children}
      <BaseSelect.Icon className="ms-2 shrink-0">
        <ChevronDown className="h-4 w-4 text-charcoal-400" />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  ),
);
SelectTrigger.displayName = "SelectTrigger";

export function SelectContent({ className, children, ...props }: React.ComponentProps<typeof BaseSelect.Popup>) {
  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner sideOffset={6} className="z-50 outline-none">
        <BaseSelect.Popup
          className={cn(
            "max-h-72 min-w-[var(--anchor-width)] overflow-y-auto rounded-md border border-charcoal-100 bg-surface p-1 shadow-elevated",
            "origin-[var(--transform-origin)] transition-all duration-150 data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
            className,
          )}
          {...props}
        >
          <BaseSelect.List>{children}</BaseSelect.List>
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

export const SelectItem = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof BaseSelect.Item>>(
  ({ className, children, ...props }, ref) => (
    <BaseSelect.Item
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm py-2 ps-8 pe-3 text-sm text-charcoal-800 outline-none",
        "data-[highlighted]:bg-primary-50 data-[highlighted]:text-primary-900 data-[selected]:font-medium",
        className,
      )}
      {...props}
    >
      <span className="absolute start-2.5 inline-flex">
        <BaseSelect.ItemIndicator>
          <Check className="h-4 w-4 text-primary-600" />
        </BaseSelect.ItemIndicator>
      </span>
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  ),
);
SelectItem.displayName = "SelectItem";
